import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { clamp, ease, progress } from '@/components/landing/film/timeline'

import {
    fixed,
    hash,
    setLayer,
    setStyle
} from '@/components/landing/film/overlay-utils'

interface SceneOpenerProps {
    register: RegisterUpdater
}

const WORDS = ['ALIF', 'LAMATO']

// Scene 01: the name explodes into place letter by letter, then golden light sweeps across it
const SceneOpener: FC<SceneOpenerProps> = ({ register }): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const charRefs = useRef<Array<HTMLSpanElement | null>>([])
    const subtitleRef = useRef<HTMLParagraphElement>(null)
    const streakRef = useRef<HTMLDivElement>(null)
    const lineRef = useRef<HTMLDivElement>(null)

    useEffect(() => register((t, _real, frame) => {
        const active = t >= 0.3 && t < 4.2
        setLayer(layerRef.current, active ? 1 : 0)
        if (!active) return

        const total = charRefs.current.length
        const center = (total - 1) / 2

        charRefs.current.forEach((element, i) => {
            if (!element) return
            const start = 0.55 + i * 0.065
            const arrive = ease.outExpo(progress(t, start, start + 1.05))
            const leave = ease.inCubic(progress(t, 3.42 + Math.abs(i - center) * 0.035, 4.05))
            const k = 1 - arrive
            const x = (hash(i, 1) - 0.5) * frame.width * 0.9 * k
            const y = (hash(i, 2) - 0.5) * frame.width * 0.32 * k
            const z = Math.min(680, (hash(i, 3) - 0.35) * 1100) * k
            const rx = (hash(i, 4) - 0.5) * 160 * k
            const ry = (hash(i, 5) - 0.5) * 220 * k
            const rz = (hash(i, 6) - 0.5) * 60 * k
            const scale = 1 + leave * 1.7
            const blur = k * 14 + leave * 10

            setStyle(element, 'transform', `translate3d(${fixed(x, 1)}px, ${fixed(y, 1)}px, ${fixed(z, 1)}px) rotateX(${fixed(rx, 1)}deg) rotateY(${fixed(ry, 1)}deg) rotateZ(${fixed(rz, 1)}deg) scale(${fixed(scale, 3)})`)
            setStyle(element, 'filter', blur > 0.05 ? `blur(${fixed(blur, 2)}px)` : 'none')
            setStyle(element, 'opacity', fixed(clamp(arrive * 1.4) * (1 - leave), 3))
        })

        const sub = ease.outCubic(progress(t, 1.55, 2.7))
        const subLeave = progress(t, 3.35, 3.85)
        setStyle(subtitleRef.current, 'opacity', fixed(sub * 0.92 * (1 - subLeave), 3))
        setStyle(subtitleRef.current, '--ls-extra', `${fixed((1 - sub) * 0.9, 3)}em`)
        setStyle(subtitleRef.current, 'clip-path', `inset(0 ${fixed((1 - sub) * 50, 2)}% 0 ${fixed((1 - sub) * 50, 2)}%)`)

        const sweepA = progress(t, 2.0, 2.85)
        const sweepB = progress(t, 2.95, 3.4)
        const sweep = sweepA > 0 && sweepA < 1 ? sweepA : sweepB
        setStyle(streakRef.current, 'transform', `translateX(${fixed(-80 + sweep * 520, 2)}%) skewX(-18deg)`)
        setStyle(streakRef.current, 'opacity', (sweepA > 0 && sweepA < 1) || (sweepB > 0 && sweepB < 1) ? '1' : '0')

        const line = ease.outExpo(progress(t, 1.9, 2.6))
        setStyle(lineRef.current, 'transform', `scaleX(${fixed(line, 3)})`)
        setStyle(lineRef.current, 'opacity', fixed(0.9 * (1 - progress(t, 3.2, 3.9)), 3))
    }), [register])

    let index = 0

    return (
        <div ref={layerRef} className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center' style={{ opacity: 0, visibility: 'hidden' }}>
            <div className='relative'>
                <p className='film-title text-titanium-light' aria-hidden='true'>
                    {WORDS.map((word) => (
                        <span key={word} className='film-title-word' aria-hidden='true'>
                            {Array.from(word).map((char) => {
                                const current = index++
                                return (
                                    <span
                                        key={`${word}-${current}`}
                                        ref={(element) => {
                                            charRefs.current[current] = element
                                        }}
                                        className='film-char text-titanium-metal'
                                        style={{ opacity: 0 }}
                                    >
                                        {char}
                                    </span>
                                )
                            })}
                        </span>
                    ))}
                </p>

                <div className='pointer-events-none absolute inset-y-[-25%] left-0 w-full overflow-hidden mix-blend-color-dodge' aria-hidden='true'>
                    <div
                        ref={streakRef}
                        className='h-full w-1/4'
                        style={{
                            opacity: 0,
                            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 196, 64, 0.35) 30%, rgba(255, 240, 190, 0.95) 50%, rgba(255, 196, 64, 0.35) 70%, transparent 100%)'
                        }}
                    />
                </div>
            </div>

            <div
                ref={lineRef}
                className='mt-[2.4cqh] h-px w-[62cqw] origin-center bg-linear-to-r from-transparent via-gold to-transparent'
                style={{ opacity: 0, transform: 'scaleX(0)' }}
                aria-hidden='true'
            />

            <p ref={subtitleRef} className='film-subtitle mt-[2.4cqh] text-gold' style={{ opacity: 0 }}>
                PRECISION FORGED WHEELS ATELIER
            </p>
        </div>
    )
}

export default SceneOpener
